import type { GetPostedJobResponseDto } from "@workspace/dto/job/get-posted-job";
import type { GetServerSideProps, InferGetServerSidePropsType } from "next";
import Head from "next/head";
import { NextSeo } from "next-seo";
import { getLayout } from "@/components/layouts/centered";
import { ListingHero } from "@/components/listings/listing-hero";
import { PageHeader } from "@/components/page-header";
import { getAppBaseUrl } from "@/lib/app-base-url";
import { buildJobPostingJsonLd } from "@/lib/job-posting-jsonld";
import { buildListingSlug, extractNanoId } from "@/lib/slugify";

type Job = GetPostedJobResponseDto["job"];

function stripMarkdown(text: string): string {
  return text
    .replace(/[#*_~`>()!|[\]-]/g, "")
    .replace(/\n+/g, " ")
    .trim();
}

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength - 3)}...`;
}

function getBaseUrl(host?: string): string {
  if (host?.includes("https") || host?.includes("http")) {
    return host;
  }
  if (host?.includes("localhost")) {
    return "http://localhost:3000";
  }
  if (!host?.includes("https")) {
    return `https://${host}`;
  }
  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`;
  }
  return "http://localhost:3000";
}

type ListingPageProps = {
  job: Job;
  canonicalUrl: string;
  jsonLd: Record<string, unknown>;
  seo: {
    title: string;
    description: string;
    ogImageUrl: string;
  };
};

export const getServerSideProps: GetServerSideProps<ListingPageProps> = async (
  ctx
) => {
  const headers = ctx.req?.headers;
  const host = getBaseUrl(headers?.host);

  const appUrl = getAppBaseUrl(host);

  const urlSlug = ctx.params?.slug as string;
  const nanoId = extractNanoId(urlSlug);

  const url = `${host}/api/jobs/listings/${nanoId}/get-job-listing`;
  const res = await fetch(url);
  if (!res.ok) {
    return { notFound: true };
  }

  const data: GetPostedJobResponseDto = await res.json();
  const job = data.job;

  const orgName = job.postedBy ?? "unknown";
  const canonicalSlug = buildListingSlug(job.slug, job.title, orgName);

  if (urlSlug !== canonicalSlug) {
    return {
      redirect: {
        destination: `/job-listings/${canonicalSlug}`,
        permanent: true,
      },
    };
  }

  const canonicalUrl = `${appUrl}/job-listings/${canonicalSlug}`;
  const ogImageUrl = `${appUrl}/api/og?slug=${job.slug}`;
  const jsonLd = buildJobPostingJsonLd(job, canonicalUrl, ogImageUrl);
  const title = `${job.title} at ${orgName} | VacWork`;
  const description = truncate(
    stripMarkdown(job.description || job.title),
    160
  );

  return {
    props: {
      job: structuredClone(job),
      canonicalUrl,
      jsonLd,
      seo: {
        title,
        description,
        ogImageUrl,
      },
    },
  };
};

export default function ListingPage({
  job,
  canonicalUrl,
  jsonLd,
  seo,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <>
      <NextSeo
        canonical={canonicalUrl}
        description={seo.description}
        openGraph={{
          title: seo.title,
          description: seo.description,
          url: canonicalUrl,
          siteName: "VacWork",
          locale: "en_ZA",
          type: "website",
          images: [
            {
              url: seo.ogImageUrl,
              width: 1200,
              height: 630,
            },
          ],
        }}
        title={seo.title}
        twitter={{
          cardType: "summary_large_image",
        }}
      />
      <Head>
        <script
          // biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD structured data, escaped below
          dangerouslySetInnerHTML={{
            // Escape `<` so employer-controlled fields containing `</script>`
            // can't break out of the script tag (XSS). JSON.stringify alone
            // does not HTML-escape.
            __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c"),
          }}
          type="application/ld+json"
        />
      </Head>
      <PageHeader />
      <main className="mx-auto mt-20 mb-20 h-full w-full max-w-5xl px-4 pb-8 sm:px-6">
        <ListingHero listing={job} />
      </main>
    </>
  );
}

ListingPage.getLayout = getLayout;
